import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AspiringLayout from "@/components/AspiringLayout";
import { SolutionTab } from "@/components/aspiring-tabs/SolutionTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOLUTION_FIELDS = ["solution_description", "technology_approach", "development_stage", "why_you", "defensibility", "vs_standard_of_care"];

function SolutionContent({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [profile, setProfile] = useState({});

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/aspiring/profile`, { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then(d => setProfile(d.profile || {}))
      .finally(() => setLoading(false));
  }, []);

  const save = async (data = profile) => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/aspiring/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data)
      });
      toast[r.ok ? "success" : "error"](r.ok ? "Saved!" : "Failed");
    } catch {
      toast.error("Failed");
    }
    setSaving(false);
  };

  const clear = async () => {
    if (!confirm("Are you sure you want to clear this section?")) return;
    setClearing(true);
    const clearedProfile = { ...profile };
    SOLUTION_FIELDS.forEach(f => { clearedProfile[f] = ""; });
    setProfile(clearedProfile);
    await save(clearedProfile);
    toast.success("Section cleared");
    setClearing(false);
  };

  const update = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="aspiring-solution-page">
      <SolutionTab profile={profile} update={update} />
      <div className="flex gap-3">
        <Button 
          onClick={clear} 
          disabled={clearing || saving} 
          variant="outline"
          className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="clear-solution-btn"
        >
          {clearing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          <Trash2 className="w-4 h-4 mr-2" />
          Clear
        </Button>
        <Button 
          onClick={() => save()} 
          disabled={saving || clearing} 
          className="flex-1 bg-amber-500 hover:bg-amber-600"
          data-testid="save-solution-btn"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          <Save className="w-4 h-4 mr-2" />
          Save
        </Button>
      </div>
    </div>
  );
}

export default function AspiringSolutionPage() {
  return <AspiringLayout>{({ user }) => <SolutionContent user={user} />}</AspiringLayout>;
}
